<template>
  <div>
    <EventModal
      ref="modalRef"
      v-model:visible-event-modal="eventModalVisible"
      :event="ihFailureNotice"
      :event-type="EventType.IHFailureNotice"
      :title="
        modalTitle ??
        (isCreate
          ? t('components.issueModal.newFailureNotice').toString()
          : t('components.issueModal.updateFailureNotice').toString())
      "
      :save-and-new-button-title="
        isCreate
          ? t('components.issueModal.createAndTask').toString()
          : t('components.issueModal.updateAndTask').toString()
      "
      :hide-add-task-button="hideAddTaskButton"
      :loading="loading"
      :original-owner-id="event?.ownerId"
      @save="save"
      @select-technical-place="setTechnicalPlace"
      @select-technical-category="setTechnicalCategory"
      @abort="$emit('abort')"
      @cancel="$emit('cancel')"
      @update:visible-event-modal="$emit('update:visible', $event)"
      @observer:valid="observerValid = $event"
    >
      <template #header>
        <slot name="header" />
      </template>
    </EventModal>
    <CreateTaskModal
      v-model:visible-action-modal="taskModalVisible"
      :event="ihFailureNotice"
      @save="taskModalVisible = false"
      @hide="taskModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { provide, ref, nextTick, type PropType, computed, inject } from "vue";
import { DateTime } from "luxon";
import { useStore } from "vuex";
import {
  useUpdateIHFailureNoticeMutation,
  PriorityType,
  useCreateIHFailureNoticeMutation,
  EventType,
  type IEventOption,
  IHFailureNoticeType,
  EisenhowerPriority,
} from "@/graphql";
import EventModal from "@/components/event-action/event/modal/EventModal.vue";
import { CreateInjectionKey } from "@/components/event-action/issue/IssueTypes";
import {
  DefaultFlagInjectionKey,
  DefaultDateInjectionKey,
  TechnicalPlaceForEventModalInjectionKey,
  DefaultEisenhowerPriorityInjectionKey,
  BoardIdInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import CreateTaskModal from "@/components/event-action/task/modal/CreateTaskModal.vue";

import { addEvent } from "@/graphql/apolloCacheUtils";
import { useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import type { IHFailureNoticeEditType } from "@/components/event-action/event/modal/EventEditType";

defineOptions({
  inheritAttrs: true,
});
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<IHFailureNoticeEditType | null>,
    required: false,
    default: null,
  },
  hideAddTaskButton: {
    type: Boolean,
    default: false,
  },
  modalTitle: {
    type: String,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits([
  "save",
  "createEvent",
  "updateEvent",
  "abort",
  "cancel",
  "update:visible",
]);
const store = useStore();
const observerValid = ref(false);

const eventModalVisible = ref(props.visible);
const taskModalVisible = ref(false);

const injectedBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const boardId = computed(() => {
  const id =
    props.event?.boardConnection?.find((con) => con.status === 0)?.boardId ?? injectedBoardId.value;
  if (id == null) {
    return -1; // when modal is open on board change the board is is for a split second null and that would collide and i dont know any better solution right no
  }
  return id;
});

const { board } = useSpecifiedBoardContext(boardId);

const technicalPlaceIdForEventModal = inject(TechnicalPlaceForEventModalInjectionKey, null);

const ihFailureNoticeType = ref(IHFailureNoticeType.COMMON);

const defaultFlags = inject(DefaultFlagInjectionKey, ref([]));
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: DateTime.now().toISO(), editable: true })),
);

const defaultEisenhowerPriority = inject(
  DefaultEisenhowerPriorityInjectionKey,
  ref(EisenhowerPriority.NO_PRIORITY),
);

const defaultTechnicalCategoryId = computed(
  () => technicalPlaceIdForEventModal?.value?.id ?? board?.value?.technicalPlaceId,
);

function defaultFailureNotice(defaultTpId?: number): IHFailureNoticeEditType {
  return {
    description: "",
    ownerId: store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"],
    deadline: defaultDate.value.isoString,
    technicalPlaceId: undefined,
    technicalCategoryId: defaultTpId ?? defaultTechnicalCategoryId.value,
    priority: PriorityType.standard,
    failure: false,
    faultTimeStart: DateTime.now().toISO(),
    faultTimeEnd: undefined,
    planDate: undefined,
    flags: defaultFlags.value.map((flag) => ({
      id: flag.id,
    })),
    fileIds: [],
    cost: undefined,
    failureNoticeType: ihFailureNoticeType.value,
    dynamicDate: undefined,
    dynamicResponsibleId: undefined,
    eisenhowerPriority: defaultEisenhowerPriority.value,
  };
}

const ihFailureNotice = usePropsAsDefault({
  dynamicProps: computed(() => {
    return props.event ?? defaultFailureNotice(defaultTechnicalCategoryId.value);
  }),
  replace: true,
});

const isCreate = computed(() => props?.event?.id == null);
provide(CreateInjectionKey, isCreate);
const { mutate: createIHFailureNoticeMutation, loading: loadingCreate } =
  useCreateIHFailureNoticeMutation({});
const { mutate: updateIHFailureNoticeMutation, loading: loadingUpdate } =
  useUpdateIHFailureNoticeMutation({});
const loading = computed(() => loadingCreate.value || loadingUpdate.value);
const createIHFailureNotice = async (openTask: boolean) => {
  if (observerValid.value !== true) {
    return;
  }
  const tpId = ihFailureNotice.value.technicalPlaceId ?? defaultTechnicalCategoryId.value;

  if (tpId == null || ihFailureNotice.value.technicalCategoryId == null) {
    throw Error("technicalPlace was not selected");
  }
  const saveIHFailureNoticeObject = {
    ...ihFailureNotice.value,
    type: undefined,
    flags: ihFailureNotice.value.flags.map((flag) => ({
      id: flag.id,
    })),
    cost: ihFailureNotice.value.cost,
    faultTimeStart: ihFailureNotice.value.faultTimeStart.toString(),
    failureNoticeType: undefined,
    technicalPlaceId: tpId,
    technicalCategoryId: ihFailureNotice.value.technicalCategoryId,
  };
  if ("dynamicDate" in saveIHFailureNoticeObject) {
    delete saveIHFailureNoticeObject.dynamicDate;
  }
  if ("dynamicResponsibleId" in saveIHFailureNoticeObject) {
    delete saveIHFailureNoticeObject.dynamicResponsibleId;
  }
  if ("document" in saveIHFailureNoticeObject) {
    delete saveIHFailureNoticeObject.document;
  }

  if (!board?.value) {
    throw Error("board is missing");
  }

  const res = await createIHFailureNoticeMutation(
    {
      ihFailureNotice: saveIHFailureNoticeObject,
      type: ihFailureNoticeType.value,
      boardId: board?.value.id,
      siteId: board?.value.siteId,
      ihConfigShortcutAdditions: {
        dynamicDate:
          ("dynamicDate" in ihFailureNotice.value && ihFailureNotice.value.dynamicDate) ||
          undefined,
        dynamicResponsibleId:
          ("dynamicResponsibleId" in ihFailureNotice.value &&
            ihFailureNotice.value.dynamicResponsibleId) ||
          undefined,
      },
    },
    {
      update: (_cache, { data }) => {
        if (data && board.value) {
          addEvent(data.CreateIHFailureNotice, board?.value.id);
        }
      },
    },
  );

  if (res?.data?.CreateIHFailureNotice == null) {
    throw Error("invalid response");
  }
  emit("save", {
    eventId: res.data.CreateIHFailureNotice.id,
    eventType: EventType.IHFailureNotice,
  });
  await nextTick();
  eventModalVisible.value = false;
  if (openTask === true) {
    ihFailureNotice.value = {
      ...res.data.CreateIHFailureNotice,
      fileIds: res.data.CreateIHFailureNotice.Folder?.files.map((file) => file.id) ?? [],
    };
    taskModalVisible.value = true;
  }
  const eventIdentifier: IEventOption = {
    eventId: res.data.CreateIHFailureNotice.id,
    eventType: EventType.IHFailureNotice,
  };

  emit("createEvent", eventIdentifier, openTask);
};
const updateIHFailureNotice = async (openTask: boolean) => {
  if (observerValid.value !== true || props.event?.id == null) {
    return;
  }
  const res = await updateIHFailureNoticeMutation({
    ihFailureNoticeId: props.event.id,
    boardId: board?.value?.id as number,
    ihFailureNotice: {
      technicalPlaceId: ihFailureNotice.value.technicalPlaceId!,
      description: ihFailureNotice.value.description,
      deadline: ihFailureNotice.value.deadline,
      ownerId: ihFailureNotice.value.ownerId,
      faultTimeStart: ihFailureNotice.value.faultTimeStart as string,
      faultTimeEnd: ihFailureNotice.value.faultTimeEnd,
      failure: ihFailureNotice.value.failure,
      priority: ihFailureNotice.value.priority,
      planDate: ihFailureNotice.value.planDate,
      flags: ihFailureNotice.value.flags.map((flag) => ({
        id: flag.id,
      })),
      cost: ihFailureNotice.value.cost,
      technicalCategoryId: ihFailureNotice.value.technicalCategoryId!,
      fileIds: ihFailureNotice.value.fileIds,
      eisenhowerPriority: ihFailureNotice.value.eisenhowerPriority,
    },
  });
  if (res?.data?.UpdateIHFailureNotice == null) {
    throw Error("invalid response");
  }
  eventModalVisible.value = false;
  if (openTask === true) {
    ihFailureNotice.value = {
      ...res.data.UpdateIHFailureNotice,
      fileIds: res.data.UpdateIHFailureNotice.Folder?.files.map((file) => file.id) ?? [],
    };
    await nextTick();
    taskModalVisible.value = true;
  }
  const eventIdentifier: IEventOption = {
    eventId: res.data.UpdateIHFailureNotice.id,
    eventType: EventType.IHFailureNotice,
  };
  emit("updateEvent", eventIdentifier, openTask);
};

const customHeader = ref<string>();

const save = computed(() => (isCreate.value ? createIHFailureNotice : updateIHFailureNotice));
const show = (options?: {
  type?: IHFailureNoticeType;
  defaultFailureNotice: Partial<ReturnType<typeof defaultFailureNotice>>;
  header?: string;
}) => {
  customHeader.value = options?.header;
  if (options?.type != null) {
    ihFailureNoticeType.value = options.type;
  } else {
    ihFailureNoticeType.value = IHFailureNoticeType.COMMON;
  }
  if (props.event == null) {
    ihFailureNotice.value.failureNoticeType = ihFailureNoticeType.value;
  }
  if (props.event == null) {
    ihFailureNotice.value = {
      ...defaultFailureNotice(),
      ...(options?.defaultFailureNotice ?? {}),
      failureNoticeType: ihFailureNoticeType.value,
    };
  } else {
    ihFailureNotice.value = JSON.parse(JSON.stringify(props.event));
  }
  eventModalVisible.value = true;
};

function setTechnicalCategory(value: number) {
  ihFailureNotice.value.technicalCategoryId = value;
}

function setTechnicalPlace(value: number) {
  ihFailureNotice.value.technicalPlaceId = value;
}

defineExpose({
  show,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
